import { FC, useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import type { AnyAction } from '@reduxjs/toolkit';
import {
  Address,
  CheckoutProfile,
  CheckoutProfileService,
  CheckoutServiceLocation,
  IkeyStringWithKeyStringObject,
  ServiceLocation,
  checkoutFulfillmentOptions,
  getFulfillmentCmsDataSliceReq,
} from '@commerce-checkout-webcomponents/sdk';
import { Contact } from '@commerce-profile-webcomponents/sdk';
import { Cart } from '@commerce-cart-webcomponents/sdk';
import { ContactType, ICheckoutLocationHeaderProps } from '../types/propTypes';
import { CheckoutShippingDetailsContainer } from '../CheckoutShippingDetailsContainer';
import { DataFlowEnum } from '../../common/types/CheckoutType';
import { ShippingTypeEnums } from '../../common/config/checkoutConstants';
import { transformCartDataToDownlinersList } from '../../common/helpers/CheckoutShippingHelpers';

export type ILocationAddress = ((Address & ContactType) | Contact) | ServiceLocation | CheckoutServiceLocation;

export const CheckoutLocationHeader: FC<ICheckoutLocationHeaderProps> = ({
  onConfirmClick,
}: ICheckoutLocationHeaderProps) => {
  const cart: Cart = useSelector((state: AppState) => state?.commerceCart?.cart || null);
  const [checkoutProfileData, setCheckoutProfileData] = useState<CheckoutProfile>(
    CheckoutProfileService.getCheckoutProfile(),
  );
  const fulfillmentCmsData: IkeyStringWithKeyStringObject = useSelector(
    (state: AppState) => state.commerceCheckout.fulfillmentCmsContent,
  );
  const dispatch = useDispatch<AppDispatch>();

  const CheckoutShippingDetailsContainerData = useMemo(
    () => ({
      shippingType:
        checkoutProfileData?.fulfillmentType?.id != checkoutFulfillmentOptions.HOMEDELIVERY
          ? ShippingTypeEnums.PICKUP
          : ShippingTypeEnums.DELIVERY,
      deliveryAddress: checkoutProfileData?.fulfillmentType?.serviceLevel?.deliveryAddress,
      storePickupAddress:
        checkoutProfileData?.fulfillmentType?.id != checkoutFulfillmentOptions.HOMEDELIVERY
          ? checkoutProfileData?.fulfillmentType?.serviceLocation
          : null,
      onDeliveryAddressConfirmClick: (args: ILocationAddress) =>
        confirmClickHandler(args, checkoutFulfillmentOptions.HOMEDELIVERY),
      onPickupAddressConfirmClick: (storeData: ILocationAddress, selectedStoreId: string) =>
        confirmClickHandler(storeData, selectedStoreId),
      shippingTypeChangeFlow: true,
      pickupContactFieldEnabled: false,
      typePickupDisabled: false,
      typeDeliveryDisabled: false,
      onAddressSelectionConfirmClick: (shippingType: string) => onChangeShippingType(shippingType),
      ...(cart?.subCarts?.length && { ...transformCartDataToDownlinersList(cart), cartData: cart }),
      fulfillmentId: checkoutProfileData?.fulfillmentType?.id,
    }),
    [checkoutProfileData],
  );
  const confirmClickHandler = (data: ILocationAddress, shippingType: string) => {
    CheckoutProfileService.updateShippingType(shippingType);
    if (shippingType == checkoutFulfillmentOptions.HOMEDELIVERY) {
      CheckoutProfileService.updateDeliveryAddress(data as Address);
    } else {
      const pickupData = data as ServiceLocation;
      CheckoutProfileService.updateServiceLocation(pickupData);
    }
    setCheckoutProfileData(CheckoutProfileService.getCheckoutProfile());
    onConfirmClick();
  };

  const onChangeShippingType = (shippingType: string) => {
    shippingType == ShippingTypeEnums.DELIVERY &&
      CheckoutProfileService.updateShippingType(checkoutFulfillmentOptions.HOMEDELIVERY);
    setCheckoutProfileData(CheckoutProfileService.getCheckoutProfile());
    onConfirmClick();
  };

  useEffect(() => {
    if (!fulfillmentCmsData || !Object.keys(fulfillmentCmsData)?.length) {
      dispatch(getFulfillmentCmsDataSliceReq() as unknown as AnyAction);
    }
  }, []);

  return (
    <CheckoutShippingDetailsContainer dataFlow={DataFlowEnum.EXTERNAL} data={CheckoutShippingDetailsContainerData} />
  );
};

export default CheckoutLocationHeader;
